/**
 * SharePoint integration store.
 *
 * Owns:
 *  - The current user's SharePoint connection state
 *  - The browse navigation (sites → drives → files), shared by both the
 *    "Import from SharePoint" dialog (pick mode) and the "Save to SharePoint"
 *    dialog (save mode)
 *  - Selection state (pick mode) and destination state (save mode)
 *  - Job polling for active import/export jobs
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  disconnectSharePoint,
  exportToSharePoint,
  getSharePointAuthUrl,
  getSharePointJobStatus,
  getSharePointSiteByUrl,
  getSharePointStatus,
  importSharePointFiles,
  listSharePointDrives,
  listSharePointItems,
  listSharePointSites,
  type SharePointConnectionSummary,
  type SharePointDrive,
  type SharePointExportRequest,
  type SharePointImportDestination,
  type SharePointItem,
  type SharePointJobStatusResponse,
  type SharePointSite,
} from '@/lib/api'
import { routeTemplateExportError } from '@/stores/export-templates-store'

// ===================
// Types
// ===================

export type BrowserMode = 'pick' | 'save'

export interface BreadcrumbCrumb {
  id: string
  name: string
}

export interface SaveDestination {
  siteId: string
  driveId: string
  folderId: string
  folderPath: string | null
}

export type ActiveJob =
  | { kind: 'import'; jobId: string; status: SharePointJobStatusResponse }
  | { kind: 'export'; jobId: string; status: SharePointJobStatusResponse }
  | null

// ===================
// State
// ===================

interface SharePointState {
  // Connection
  connection: SharePointConnectionSummary | null
  isConnected: boolean
  isCheckingConnection: boolean
  /** Epoch ms of the last authoritative connection check, or null if never checked. */
  connectionCheckedAt: number | null

  // Browser
  sites: SharePointSite[]
  drives: SharePointDrive[]
  items: SharePointItem[]
  currentSiteId: string | null
  currentDriveId: string | null
  currentFolderId: string | null
  breadcrumb: BreadcrumbCrumb[]
  nextPage: string | null
  isLoadingSites: boolean
  isLoadingDrives: boolean
  isLoadingItems: boolean

  // Browser mode
  mode: BrowserMode | null
  /** Which server-side items profile the browse calls use. `'chat'` widens the
   *  allowed file types to what chat can attach; `'import'` is the tender-doc set. */
  itemsProfile: 'import' | 'chat'
  /** Attach flows re-select files that are already in the library — attaching is
   *  not importing, so "already imported" must stay selectable there. */
  allowAlreadyImported: boolean

  // Pick mode (import)
  selectedItems: Map<string, SharePointItem>

  // Save mode (export)
  saveDestination: SaveDestination | null
  saveFilename: string
  saveConflictBehavior: 'rename' | 'replace' | 'fail'

  // Job polling
  activeJob: ActiveJob
  /** Jobs the user backgrounded. Their poll loops keep running but stop writing
   *  `activeJob`, so a dismissed job can't reappear over whatever opens next.
   *  A set, not a single id: two jobs can be in flight at once (a slow import
   *  backgrounded while an export runs), and each must stay dismissed. */
  dismissedJobIds: Set<string>

  // Errors
  error: string | null

  // ===================
  // Actions
  // ===================
  /** Cached for 5 minutes; pass `{ force: true }` to bypass the cache. */
  checkConnection: (opts?: { force?: boolean }) => Promise<void>
  beginConnect: (returnTo?: string) => Promise<void>
  disconnect: () => Promise<void>

  /** Per-open config; both options reset to their import-mode defaults on every open. */
  openBrowser: (
    mode: BrowserMode,
    opts?: { itemsProfile?: 'import' | 'chat'; allowAlreadyImported?: boolean },
  ) => void
  closeBrowser: () => void

  fetchSites: (query?: string) => Promise<void>
  /** Add a single site to the visible list and select it. Used by the
   *  "Enter SharePoint URL" input — bypasses Graph search indexing entirely. */
  resolveSiteByUrl: (url: string) => Promise<void>
  selectSite: (siteId: string) => Promise<void>
  selectDrive: (driveId: string, driveName: string) => Promise<void>
  navigateToFolder: (folderId: string, folderName: string) => Promise<void>
  navigateBackTo: (crumbIndex: number) => Promise<void>
  loadNextPage: () => Promise<void>

  // Pick mode
  toggleItemSelection: (item: SharePointItem) => void
  clearSelection: () => void
  importSelected: (destination: SharePointImportDestination) => Promise<void>

  // Save mode
  setSaveDestination: (driveId: string, folderId: string, folderPath: string | null) => void
  setSaveFilename: (filename: string) => void
  setSaveConflictBehavior: (behavior: 'rename' | 'replace' | 'fail') => void
  exportArtifact: (params: Omit<SharePointExportRequest, 'destination' | 'filename' | 'conflictBehavior'>) => Promise<void>

  // Job polling — every 2s for first 90s, back off to 10s after
  pollJob: (jobId: string, kind: 'import' | 'export') => Promise<void>
  clearActiveJob: () => void

  // Error handling
  markTokenExpired: () => void
  clearError: () => void

  // Internal — shared by selectDrive / navigateToFolder / navigateBackTo
  refreshItems: (driveId: string, folderId: string) => Promise<void>
}

// ===================
// Helpers
// ===================

function generateIdempotencyKey(): string {
  // Crypto.randomUUID is available in all evergreen browsers + Node 18+.
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function isTerminalJobStatus(status: string): boolean {
  return status === 'completed' || status === 'failed'
}

// ===================
// Store
// ===================

export const useSharePointStore = create<SharePointState>()(
  devtools(
    (set, get) => ({
      // Initial state
      connection: null,
      isConnected: false,
      isCheckingConnection: false,
      connectionCheckedAt: null,
      sites: [],
      drives: [],
      items: [],
      currentSiteId: null,
      currentDriveId: null,
      currentFolderId: null,
      breadcrumb: [],
      nextPage: null,
      isLoadingSites: false,
      isLoadingDrives: false,
      isLoadingItems: false,
      mode: null,
      itemsProfile: 'import',
      allowAlreadyImported: false,
      selectedItems: new Map(),
      saveDestination: null,
      saveFilename: '',
      saveConflictBehavior: 'rename',
      activeJob: null,
      dismissedJobIds: new Set(),
      error: null,

      // ===================
      // Connection
      // ===================

      checkConnection: async (opts?: { force?: boolean }) => {
        const TTL_MS = 5 * 60 * 1000
        const { connectionCheckedAt } = get()
        if (!opts?.force && connectionCheckedAt !== null && Date.now() - connectionCheckedAt < TTL_MS) {
          return
        }
        set({ isCheckingConnection: true })
        try {
          const status = await getSharePointStatus()
          set({
            connection: status.connection,
            isConnected: status.connected,
            isCheckingConnection: false,
            connectionCheckedAt: Date.now(),
          })
        } catch (err) {
          set({
            isCheckingConnection: false,
            error: err instanceof Error ? err.message : 'Failed to check SharePoint status',
          })
        }
      },

      beginConnect: async (returnTo?: string) => {
        try {
          const { authUrl } = await getSharePointAuthUrl(returnTo)
          window.location.href = authUrl
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Failed to start OAuth flow' })
        }
      },

      disconnect: async () => {
        try {
          await disconnectSharePoint()
          // State is authoritative post-disconnect — no need to re-check.
          set({ connection: null, isConnected: false, connectionCheckedAt: Date.now() })
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Failed to disconnect' })
        }
      },

      // ===================
      // Browser
      // ===================

      openBrowser: (mode, opts) => {
        set({
          mode,
          sites: [],
          drives: [],
          items: [],
          currentSiteId: null,
          currentDriveId: null,
          currentFolderId: null,
          breadcrumb: [],
          nextPage: null,
          selectedItems: new Map(),
          saveDestination: null,
          error: null,
          // Always written, never merged — a config left over from the previous
          // open would silently change what the next dialog allows.
          itemsProfile: opts?.itemsProfile ?? 'import',
          allowAlreadyImported: opts?.allowAlreadyImported ?? false,
        })
      },

      closeBrowser: () => {
        set({ mode: null })
      },

      fetchSites: async (query) => {
        set({ isLoadingSites: true, error: null })
        try {
          const { sites } = await listSharePointSites(query)
          set({ sites, isLoadingSites: false })
        } catch (err) {
          set({
            isLoadingSites: false,
            error: err instanceof Error ? err.message : 'Failed to load SharePoint sites',
          })
        }
      },

      resolveSiteByUrl: async (url) => {
        set({ isLoadingSites: true, error: null })
        try {
          const { site } = await getSharePointSiteByUrl(url.trim())
          // Add to visible list (deduped by id) and immediately select.
          const { sites } = get()
          const next = sites.find((s) => s.id === site.id) ? sites : [site, ...sites]
          set({ sites: next, isLoadingSites: false })
          await get().selectSite(site.id)
        } catch (err) {
          set({
            isLoadingSites: false,
            error: err instanceof Error ? err.message : 'Could not find that SharePoint site',
          })
        }
      },

      selectSite: async (siteId) => {
        set({ currentSiteId: siteId, isLoadingDrives: true, drives: [], items: [] })
        try {
          const { drives } = await listSharePointDrives(siteId)
          set({ drives, isLoadingDrives: false })
        } catch (err) {
          set({
            isLoadingDrives: false,
            error: err instanceof Error ? err.message : 'Failed to load drives',
          })
        }
      },

      selectDrive: async (driveId, driveName) => {
        set({
          currentDriveId: driveId,
          currentFolderId: 'root',
          breadcrumb: [{ id: 'root', name: driveName }],
          items: [],
          nextPage: null,
        })
        await get().refreshItems(driveId, 'root')
      },

      navigateToFolder: async (folderId, folderName) => {
        const { breadcrumb, currentDriveId } = get()
        if (!currentDriveId) return
        const newCrumbs = [...breadcrumb, { id: folderId, name: folderName }]
        set({
          currentFolderId: folderId,
          breadcrumb: newCrumbs,
          items: [],
          nextPage: null,
        })
        await get().refreshItems(currentDriveId, folderId)
      },

      navigateBackTo: async (crumbIndex) => {
        const { breadcrumb, currentDriveId } = get()
        if (!currentDriveId) return
        const target = breadcrumb[crumbIndex]
        if (!target) return
        const newCrumbs = breadcrumb.slice(0, crumbIndex + 1)
        set({
          currentFolderId: target.id,
          breadcrumb: newCrumbs,
          items: [],
          nextPage: null,
        })
        await get().refreshItems(currentDriveId, target.id)
      },

      loadNextPage: async () => {
        const { currentDriveId, currentFolderId, nextPage, items, itemsProfile } = get()
        if (!currentDriveId || !nextPage) return
        set({ isLoadingItems: true })
        try {
          const result = await listSharePointItems(
            currentDriveId,
            currentFolderId ?? 'root',
            nextPage,
            itemsProfile === 'chat' ? 'chat' : undefined,
          )
          set({
            items: [...items, ...result.items],
            nextPage: result.nextPage,
            isLoadingItems: false,
          })
        } catch (err) {
          set({
            isLoadingItems: false,
            error: err instanceof Error ? err.message : 'Failed to load more items',
          })
        }
      },

      // ===================
      // Pick mode
      // ===================

      toggleItemSelection: (item) => {
        if (item.isFolder) return
        if (item.alreadyImported && !get().allowAlreadyImported) return
        const next = new Map(get().selectedItems)
        if (next.has(item.id)) {
          next.delete(item.id)
        } else {
          next.set(item.id, item)
        }
        set({ selectedItems: next })
      },

      clearSelection: () => set({ selectedItems: new Map() }),

      importSelected: async (destination) => {
        const { selectedItems, currentSiteId, currentDriveId } = get()
        if (selectedItems.size === 0 || !currentSiteId || !currentDriveId) return

        const items = Array.from(selectedItems.values()).map((item) => ({
          siteId: currentSiteId,
          driveId: currentDriveId,
          itemId: item.id,
        }))

        try {
          const response = await importSharePointFiles(
            { destination, items },
            generateIdempotencyKey(),
          )
          if (response.jobId) {
            set({
              activeJob: {
                kind: 'import',
                jobId: response.jobId,
                status: {
                  kind: 'import',
                  status: 'pending',
                  itemsTotal: response.itemsTotal,
                  itemsDone: 0,
                  itemsFailed: 0,
                  itemsSkipped: 0,
                  errorMessage: null,
                  completedAt: null,
                },
              },
            })
            void get().pollJob(response.jobId, 'import')
          }
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Import failed to start' })
        }
      },

      // ===================
      // Save mode
      // ===================

      setSaveDestination: (driveId, folderId, folderPath) => {
        const { currentSiteId } = get()
        if (!currentSiteId) return
        set({
          saveDestination: { siteId: currentSiteId, driveId, folderId, folderPath },
        })
      },

      setSaveFilename: (saveFilename) => set({ saveFilename }),

      setSaveConflictBehavior: (saveConflictBehavior) => set({ saveConflictBehavior }),

      exportArtifact: async (params) => {
        const { saveDestination, saveFilename, saveConflictBehavior } = get()
        if (!saveDestination || !saveFilename) {
          set({ error: 'Pick a destination folder and filename first' })
          return
        }

        try {
          const response = await exportToSharePoint(
            {
              ...params,
              destination: saveDestination,
              filename: saveFilename,
              conflictBehavior: saveConflictBehavior,
            },
            generateIdempotencyKey(),
          )
          set({
            activeJob: {
              kind: 'export',
              jobId: response.jobId,
              status: {
                kind: 'export',
                status: 'pending',
                filename: saveFilename,
                errorMessage: null,
                completedAt: null,
              },
            },
          })
          void get().pollJob(response.jobId, 'export')
        } catch (err) {
          // A template that would replace part of itself asks first (R3 D4);
          // "Export anyway" resends this save with the acknowledgement.
          const retry = () => void get().exportArtifact({ ...params, acknowledgeTemplateLoss: true })
          if (routeTemplateExportError(err, retry)) return
          set({ error: err instanceof Error ? err.message : 'Export failed to start' })
        }
      },

      // ===================
      // Job polling
      // ===================

      pollJob: async (jobId, kind) => {
        const startedAt = Date.now()

        const step = async (): Promise<void> => {
          let status: SharePointJobStatusResponse
          try {
            status = await getSharePointJobStatus(jobId)
          } catch (err) {
            set({ error: err instanceof Error ? err.message : 'Failed to poll job' })
            return
          }

          // A backgrounded job keeps polling to terminal, but must not write
          // `activeJob` — that would resurrect its dialog over the next one.
          if (!get().dismissedJobIds.has(jobId)) {
            set({ activeJob: { kind, jobId, status } as ActiveJob })
          }

          if (isTerminalJobStatus(status.status)) return

          // 2s for the first 90s, then back off to 10s.
          const elapsedMs = Date.now() - startedAt
          const nextDelayMs = elapsedMs < 90_000 ? 2_000 : 10_000
          setTimeout(() => { void step() }, nextDelayMs)
        }

        await step()
      },

      clearActiveJob: () =>
        set((state) => {
          const jobId = state.activeJob?.jobId
          if (!jobId) return { activeJob: null }
          return { activeJob: null, dismissedJobIds: new Set(state.dismissedJobIds).add(jobId) }
        }),

      // ===================
      // Error handling
      // ===================

      markTokenExpired: () => {
        set({
          connection: get().connection ? { ...get().connection!, status: 'expired' } : null,
          isConnected: false,
        })
      },

      clearError: () => set({ error: null }),

      // ===================
      // Internal — fetch items helper shared by selectDrive / navigateToFolder
      // ===================

      refreshItems: async (driveId: string, folderId: string) => {
        const { itemsProfile } = get()
        set({ isLoadingItems: true })
        try {
          const result = await listSharePointItems(
            driveId,
            folderId,
            undefined,
            itemsProfile === 'chat' ? 'chat' : undefined,
          )
          set({ items: result.items, nextPage: result.nextPage, isLoadingItems: false })
        } catch (err) {
          set({
            isLoadingItems: false,
            error: err instanceof Error ? err.message : 'Failed to load items',
          })
        }
      },
    }),
    { name: 'sharepoint-store' }
  )
)
